import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Card,
  CardContent,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewListIcon from '@mui/icons-material/ViewList';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useGetProductsQuery, useDeleteProductMutation, useImportProductsMutation } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useSelector, useDispatch } from 'react-redux';
import { setFilters } from '../../../store/slices/productsSlice';
import ProductsByCategory from './ProductsByCategory';

function ProductsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.products.filters);
  const { data, isLoading, error } = useGetProductsQuery(filters);
  const [deleteProduct] = useDeleteProductMutation();
  const [importProducts] = useImportProductsMutation();
  const [tabValue, setTabValue] = useState(0);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const enableServiceModule = company?.enableServiceModule === true;
  const token = useSelector((state) => state.auth.token);

  // Import State
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [isImportingData, setIsImportingData] = useState(false);

  const handleSearch = (e) => {
    dispatch(setFilters({ search: e.target.value }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await deleteProduct(id);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Export CSV
  const handleExport = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.29.142:5001/api';
      const response = await fetch(`${baseUrl}/products/export`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to export products.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      alert(err.message || 'An error occurred during export.');
    }
  };

  // Download Sample Template CSV
  const handleDownloadTemplate = () => {
    const headers = [
      'Barcode',
      'SKU',
      'Name',
      'Type',
      'Brand',
      'BaseUnit',
      'Category',
      'Subcategory',
      'CostPrice',
      'WholesalePrice',
      'SellingPrice',
      'OpeningStock',
      'MaxDiscountType',
      'MaxDiscountPercent',
      'MaxDiscountAmount',
      'IsActive',
      'Description',
    ];
    const csvContent =
      headers.join(',') +
      '\n' +
      '6912345678901,PROD001,Premium Coffee Beans,product,StarBucks,kg,Beverages,Coffee,12.50,15.00,18.99,100,percent,10,,TRUE,Rich aromatic coffee beans\n' +
      ',SRV100,Machine Installation,service,,hrs,Services,,0,0,50.00,0,,,null,TRUE,On-site coffee machine setup';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products_import_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Parse RFC 4180 CSV Compliant
  const parseCSV = (text) => {
    const lines = [];
    let row = [''];
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          row[row.length - 1] += '"';
          i++; // skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push('');
      } else if ((char === '\r' || char === '\n') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++; // skip \n
        }
        lines.push(row);
        row = [''];
      } else {
        row[row.length - 1] += char;
      }
    }
    if (row.length > 1 || row[0] !== '') {
      lines.push(row);
    }
    return lines;
  };

  // Handle CSV File Selection and Parsing
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsedLines = parseCSV(text);
      if (parsedLines.length < 2) {
        alert('The selected CSV file has no content or headers.');
        return;
      }

      const headers = parsedLines[0].map((h) => h.trim().toLowerCase());
      const dataRows = parsedLines.slice(1);

      const mapped = dataRows
        .map((row) => {
          const item = {};
          headers.forEach((header, index) => {
            const val = row[index] ? row[index].trim() : '';
            if (header === 'barcode') item.barcode = val;
            else if (header === 'sku') item.sku = val;
            else if (header === 'name') item.name = val;
            else if (header === 'type') item.type = val.toLowerCase();
            else if (header === 'brand') item.brand = val;
            else if (header === 'baseunit') item.baseUnit = val;
            else if (header === 'category') item.category = val;
            else if (header === 'subcategory') item.subcategory = val;
            else if (header === 'costprice') item.costPrice = val;
            else if (header === 'wholesaleprice') item.wholesalePrice = val;
            else if (header === 'sellingprice') item.sellingPrice = val;
            else if (header === 'openingstock') item.openingStock = val;
            else if (header === 'maxdiscounttype') item.maxDiscountType = val;
            else if (header === 'maxdiscountpercent') item.maxDiscountPercent = val;
            else if (header === 'maxdiscountamount') item.maxDiscountAmount = val;
            else if (header === 'isactive') item.isActive = val.toUpperCase() !== 'FALSE';
            else if (header === 'description') item.description = val;
          });
          return item;
        })
        .filter((item) => item.sku || item.name); // Filter empty rows

      setImportPreview(mapped);
    };
    reader.readAsText(file);
  };

  // Submit parsed data to backend
  const handleConfirmImport = async () => {
    if (importPreview.length === 0) return;
    setIsImportingData(true);
    try {
      const response = await importProducts({ products: importPreview }).unwrap();
      alert(response.message || 'Products imported successfully!');
      setImportOpen(false);
      setImportFile(null);
      setImportPreview([]);
    } catch (err) {
      alert(err?.data?.message || err.message || 'An error occurred during import.');
    } finally {
      setIsImportingData(false);
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading products</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: { xs: 2, sm: 0 },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#2C3E50',
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            Products Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<QrCodeScannerIcon />}
              onClick={() => navigate('/products/barcode/print')}
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                fontWeight: 600,
                color: '#2C3E50',
                borderColor: 'rgba(0,0,0,0.12)',
              }}
            >
              Print Barcodes
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => navigate('/products/catalog/print')}
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                fontWeight: 600,
                color: '#2C3E50',
                borderColor: 'rgba(0,0,0,0.12)',
              }}
            >
              Print Catalog
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                fontWeight: 600,
                color: '#2C3E50',
                borderColor: 'rgba(0,0,0,0.12)',
              }}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setImportOpen(true)}
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                fontWeight: 600,
                color: '#2C3E50',
                borderColor: 'rgba(0,0,0,0.12)',
              }}
            >
              Import
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/products/new')}
              className="primary-button"
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                fontWeight: 600,
              }}
            >
              Add Product
            </Button>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 3,
            minHeight: 48,
            '&.Mui-selected': {
              color: '#4ECDC4',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#4ECDC4',
            height: 3,
            borderRadius: 2,
          },
        }}
      >
        <Tab icon={<ViewListIcon />} label="List View" iconPosition="start" />
        <Tab icon={<CategoryIcon />} label="Category Tree" iconPosition="start" />
      </Tabs>

      {tabValue === 0 && (
        <Card
          sx={{
            background: 'white',
            border: 'none',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Search Something..."
                variant="outlined"
                value={filters.search || ''}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#7F8C8D' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: { xs: '100%', sm: 400 },
                  flex: '1 1 200px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 5,
                    bgcolor: '#F8F9FA',
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {data?.pagination
                  ? `Page ${data.pagination.page} of ${data.pagination.pages || 1} (${data.pagination.total} total)`
                  : ''}
              </Typography>
            </Box>

            <TableContainer
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#4ECDC4',
                  borderRadius: 4,
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: '#2C3E50',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        width: 56,
                      }}
                    >
                      S.No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Barcode
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      SKU
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Name
                    </TableCell>
                    {enableServiceModule && (
                      <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Type
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Brand
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: '#2C3E50',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: { xs: 'none', md: 'table-cell' },
                      }}
                    >
                      Main Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: '#2C3E50',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        display: { xs: 'none', md: 'table-cell' },
                      }}
                    >
                      Subcategory
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {`Cost Price (${currency})`}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {`Selling Price (${currency})`}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.products?.length > 0 ? (
                    data.products.map((product, index) => {
                      const page = data?.pagination?.page ?? 1;
                      const limit = data?.pagination?.limit ?? 10;
                      const serialNo = (page - 1) * limit + index + 1;
                      return (
                        <TableRow
                          key={product.id}
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: '#E8F5E9',
                              cursor: 'pointer',
                            },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <TableCell
                            sx={{
                              color: '#2C3E50',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontWeight: 500,
                            }}
                          >
                            {serialNo}
                          </TableCell>
                          <TableCell sx={{ color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {product.barcode || '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {product.sku}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#2C3E50',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                          >
                            {product.name}
                          </TableCell>
                          {enableServiceModule && (
                             <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                               <Chip
                                 label={product.type === 'service' ? 'Service' : 'Product'}
                                 sx={{
                                   bgcolor: product.type === 'service' ? '#9C27B0' : '#E0E0E0',
                                   color: product.type === 'service' ? 'white' : '#2C3E50',
                                   fontWeight: 600,
                                   borderRadius: 2,
                                   fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                 }}
                                 size="small"
                               />
                             </TableCell>
                           )}
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {product.brand || (
                              <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              display: { xs: 'none', md: 'table-cell' },
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                          >
                            {product.category ? (
                              product.category.parent ? (
                                product.category.parent.name
                              ) : (
                                product.category.name
                              )
                            ) : (
                              <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              display: { xs: 'none', md: 'table-cell' },
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                          >
                            {product.category && product.category.parent ? (
                              product.category.name
                            ) : (
                              <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: '#2C3E50', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {product.costPrice}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: '#4ECDC4',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                          >
                            {product.sellingPrice}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            <Chip
                              label={product.isActive ? 'Active' : 'Inactive'}
                              sx={{
                                bgcolor: product.isActive ? '#4ECDC4' : '#E0E0E0',
                                color: product.isActive ? 'white' : '#7F8C8D',
                                fontWeight: 600,
                                borderRadius: 2,
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                              <IconButton
                                size="small"
                                onClick={() => navigate('/products/barcode/print', { state: { product } })}
                                title="Print Barcode"
                                sx={{
                                  color: '#2C3E50',
                                  padding: { xs: 0.5, sm: 1 },
                                  '&:hover': {
                                    bgcolor: 'rgba(44, 62, 80, 0.1)',
                                  },
                                }}
                              >
                                <QrCodeScannerIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/products/${product.id}`)}
                                sx={{
                                  color: '#4ECDC4',
                                  padding: { xs: 0.5, sm: 1 },
                                  '&:hover': {
                                    bgcolor: 'rgba(78, 205, 196, 0.1)',
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(product.id)}
                                sx={{
                                  color: '#F44336',
                                  padding: { xs: 0.5, sm: 1 },
                                  '&:hover': {
                                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={enableServiceModule ? 12 : 11} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: '#7F8C8D' }}>No products found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {data?.pagination && data.pagination.pages > 0 && (
              <TablePagination
                component="div"
                count={data.pagination.total}
                page={(data.pagination.page || 1) - 1}
                onPageChange={(e, newPage) => dispatch(setFilters({ page: newPage + 1 }))}
                rowsPerPage={data.pagination.limit || 10}
                onRowsPerPageChange={(e) => {
                  const limit = parseInt(e.target.value, 10) || 10;
                  dispatch(setFilters({ limit, page: 1 }));
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && <ProductsByCategory />}

      {/* Import Modal */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#2C3E50' }}>Import Products from CSV</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV file containing your product catalog. New items will be created and items with matching SKUs or Barcodes will be updated automatically.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadTemplate}
                sx={{ borderRadius: 2 }}
              >
                Download Sample Template
              </Button>
              <Button variant="contained" component="label" startIcon={<UploadFileIcon />} sx={{ borderRadius: 2, bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3DBDB4' } }}>
                Select CSV File
                <input type="file" accept=".csv" hidden onChange={handleFileChange} />
              </Button>
            </Box>
            {importFile && (
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                Selected: {importFile.name} ({importPreview.length} rows parsed)
              </Typography>
            )}
          </Box>

          {importPreview.length > 0 && (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8F9FA' }}>
                    <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    {enableServiceModule && <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Selling</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importPreview.slice(0, 10).map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{row.sku || '–'}</TableCell>
                      <TableCell>{row.name || 'Unnamed Product'}</TableCell>
                      {enableServiceModule && (
                        <TableCell>
                          <Chip label={row.type === 'service' ? 'Service' : 'Product'} size="small" color={row.type === 'service' ? 'secondary' : 'default'} />
                        </TableCell>
                      )}
                      <TableCell>{row.category || '–'}</TableCell>
                      <TableCell>{row.costPrice || 0}</TableCell>
                      <TableCell>{row.sellingPrice || 0}</TableCell>
                      <TableCell>{row.openingStock || 0}</TableCell>
                    </TableRow>
                  ))}
                  {importPreview.length > 10 && (
                    <TableRow>
                      <TableCell colSpan={enableServiceModule ? 7 : 6} align="center" sx={{ py: 1.5, color: 'text.secondary', bgcolor: '#F9F9F9' }}>
                        ... and {importPreview.length - 10} more rows
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setImportOpen(false)} variant="outlined" sx={{ borderRadius: 2, color: '#7F8C8D', borderColor: '#BDC3C7' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            disabled={importPreview.length === 0 || isImportingData}
            sx={{ borderRadius: 2, bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3DBDB4' } }}
          >
            {isImportingData ? 'Importing...' : `Confirm Import (${importPreview.length} Items)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductsList;
